import { sharedLib } from "@shared/services/shared.service";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  STORAGE_OAUTH_DATA_KEY,
  UUID_IN_USE_CODE,
} from "@shared/common/constants";
import {
  DisplayResultMessage,
  FixMeLater,
  Platform,
  StorageOauthUserData,
} from "@shared/types";
import { GetBasicDetailsFromUuidData } from "@shared/features/onboarding/types";
import useSendAuthCode from "@shared/features/onboarding/hooks/useSendAuthCode";
import { useUser } from "@shared/utils/hooks/misc";
import { getUserOauthData } from "@shared/features/onboarding/utils";
import { useTelegramUserPreview } from "@shared/features/onboarding/hooks";

interface useValidateConnectTelegramProps {
  uuid?: string;
  shortUuid?: string;
  setUuid?: (uuid: string) => void;
  displayResultMessage: DisplayResultMessage;
  getBasicDetailsFromUuidData?: GetBasicDetailsFromUuidData;
  code?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  platform: Platform;
  requirePassword?: boolean;
}

const useValidateConnectTelegram = ({
  uuid: propsUuid,
  shortUuid,
  setUuid,
  displayResultMessage,
  getBasicDetailsFromUuidData,
  code,
  onSuccess,
  onCancel,
  platform,
}: useValidateConnectTelegramProps) => {
  const [storageUuid, setStorageUuid] = useState<string | undefined>();
  const [storageData, setStorageData] = useState<
    StorageOauthUserData | undefined
  >();
  const [connectToTelegram, connectToTelegramStatus] =
    sharedLib.sharedApi.authentication.useConnectToTelegramMutation();
  const { user, refetch: refetchGetUser } = useUser();
  const didRedirect = useRef(false);
  const uuid = propsUuid ?? storageUuid;

  const removeStorageUuid = () => {
    if (user) {
      sharedLib.storageService.removeJsonValues(STORAGE_OAUTH_DATA_KEY, [
        user.id.toString(),
        user.email,
      ]);
    }
  };

  const handleRemoveUuids = (isSuccess?: boolean) => {
    removeStorageUuid();
    setStorageUuid(undefined);
    setUuid?.("");
    if (isSuccess) {
      refetchGetUser();
      onSuccess?.();
    } else {
      onCancel?.();
    }
  };

  const { openRemoveUuidConfirmationModal, removeUuidModalData } =
    useTelegramUserPreview(handleRemoveUuids);

  const handleUuidInUse = (response: FixMeLater) => {
    if (response.status === UUID_IN_USE_CODE) {
      getBasicDetailsFromUuidData?.refetch();
    }
  };

  const {
    validateAuthCode,
    authCode,
    setInvalidAuthCodeError,
    securityCodeInputData,
    onSendAuthCodeClick,
  } = useSendAuthCode({
    shortUuid,
    uuid,
    code,
    platform,
    displayResultMessage,
    handleUuidInUse,
  });

  useEffect(() => {
    if (!storageUuid && user) {
      const data = getUserOauthData(user);
      if (data) {
        setStorageUuid(data.uuid);
        setStorageData(data);
      }
    }
  }, [user]);

  const onConnectClick = useCallback(
    (argsAuthCode?: string) => {
      const finalAuthCode = argsAuthCode ?? authCode;
      if (validateAuthCode(finalAuthCode)) {
        if ((uuid || shortUuid) && finalAuthCode) {
          displayResultMessage(
            connectToTelegram({
              user_short_uuid: shortUuid!,
              auth_code: finalAuthCode,
              uuid: uuid,
            }).unwrap(),
            "Telegram account successfully connected",
            undefined,
            undefined,
            undefined,
            [UUID_IN_USE_CODE]
          )
            .then(() => handleRemoveUuids(true))
            .catch(handleUuidInUse);
        }
      } else {
        setInvalidAuthCodeError(true);
      }
    },
    [authCode, uuid]
  );

  const callCancel =
    !didRedirect.current &&
    getBasicDetailsFromUuidData &&
    (getBasicDetailsFromUuidData.isError ||
      (!getBasicDetailsFromUuidData.isFetching &&
        !getBasicDetailsFromUuidData.data));
  useEffect(() => {
    if (callCancel) {
      didRedirect.current = true;
      onCancel?.();
    }
  }, [callCancel]);

  const continueButtonData = {
    onClick: onConnectClick,
    disabled: !authCode,
    isLoading: connectToTelegramStatus.isLoading,
  };

  return {
    onSendAuthCodeClick,
    continueButtonData,
    securityCodeInputData,
    handleRemoveUuids,
    defaultTitle: "Verify Telegram Account",
    storageData,
    openRemoveUuidConfirmationModal,
    removeUuidModalData,
  };
};

export default useValidateConnectTelegram;
